import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SongMetricsChartProps {
    data: {
      month: string;
      streams: number;
      listeners?: number;
      saves?: number;
      playlist_adds?: number;
    }[];
  }

// Wichtig: Default-Wert für data als leeres Array hinzufügen
const SongMetricsChart: React.FC<SongMetricsChartProps> = ({ data = [] }) => {
  const [metricType, setMetricType] = useState<'streams' | 'listeners' | 'saves' | 'playlist_adds'>('streams');
  
  // If no data is available, display a message
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-gray-500">No metrics history available for this song.</p>
        <p className="text-sm text-gray-400 mt-2">Add monthly metrics to see the chart.</p>
      </div>
    );
  }

  // Determine the metric label and color based on selected type
  const getMetricConfig = () => {
    switch (metricType) {
      case 'streams':
        return {
          label: 'Streams',
          color: 'rgb(22, 163, 74)', // green
          bgColor: 'rgba(22, 163, 74, 0.1)'
        };
      case 'listeners':
        return {
          label: 'Monthly Listeners',
          color: 'rgb(59, 130, 246)', // blue
          bgColor: 'rgba(59, 130, 246, 0.1)'
        };
      case 'saves':
        return {
          label: 'Saves',
          color: 'rgb(139, 92, 246)', // purple
          bgColor: 'rgba(139, 92, 246, 0.1)'
        };
      case 'playlist_adds':
        return {
          label: 'Playlist Adds',
          color: 'rgb(244, 63, 94)', // red
          bgColor: 'rgba(244, 63, 94, 0.1)'
        };
      default:
        return {
          label: 'Streams',
          color: 'rgb(22, 163, 74)',
          bgColor: 'rgba(22, 163, 74, 0.1)'
        };
    }
  };

  const metricConfig = getMetricConfig();

  // If the data doesn't have the selected metric, use streams as fallback
  const hasSelectedMetric = data.some(item => metricType in item);
  const actualMetricType = hasSelectedMetric ? metricType : 'streams';

  // Sort the data by date (month)
  const sortedData = [...data].sort((a, b) => {
    // Convert month names to dates for proper sorting
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthA = months.indexOf(a.month);
    const monthB = months.indexOf(b.month);
    return monthA - monthB;
  });

  // Korrigierter Code für den chartData-Teil - sicheres Type Handling
  const chartData = {
    labels: sortedData.map(item => item.month),
    datasets: [
      {
        label: metricConfig.label,
        // Korrekte Typenbehandlung mit Standardwert 0
        data: sortedData.map(item => {
          // Explizit überprüfen, ob die Eigenschaft existiert und einen Wert hat
          const value = item[actualMetricType as keyof typeof item];
          return typeof value === 'number' ? value : 0;
        }),
        borderColor: metricConfig.color,
        backgroundColor: metricConfig.bgColor,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: metricConfig.color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            return `${metricConfig.label}: ${value.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 0, // Minimum immer 0 setzen für übersichtlichere Charts
        // Sicherer Ansatz für max-Berechnung mit TypeScript
        max: Math.max(
          ...sortedData.map(item => {
            const value = item[actualMetricType as keyof typeof item];
            return typeof value === 'number' ? value : 0;
          })
        ) * 1.1,
        ticks: {
          stepSize: undefined,
        },
        grid: {
          drawBorder: false,
        } as any,
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        } as any,
      },
    },
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <select
          value={metricType}
          onChange={(e) => setMetricType(e.target.value as any)}
          className="block px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
        >
          <option value="streams">Streams</option>
          <option value="listeners">Monthly Listeners</option>
          <option value="saves">Saves</option>
          <option value="playlist_adds">Playlist Adds</option>
        </select>
      </div>
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default SongMetricsChart;